// The shop is a single-seller storefront: every listing is sold by the parent
// business, whichever admin account happened to create it. Shown to buyers in
// place of the per-listing sellerName (which stays visible in the admin table).
// A brand name, so it is deliberately not translated.
export const SHOP_SELLER_NAME = "Avika Ventures";
export const SHOP_SELLER_URL = "https://avikaventures.com/";

export const en = {
  common: {
    back: "Back",
    share: "Share",
    edit: "Edit",
    delete: "Delete",
    save: "Save",
    cancel: "Cancel",
    loading: "Loading...",
    search: "Search",
    clear: "Clear",
    remove: "Remove",
    or: "or",
    noResults: "No books found.",
    published: "Published",
    draft: "Draft",
  },

  header: {
    skipLink: "Skip to main content",
    logoAlt: "SaatSaheli logo",
    siteName: "Saat Saheli",
    siteTagline: "Welcome to Saat Saheli \u2013 A Community for Passion and Creativity!",
    navHome: "Home",
    navAbout: "About",
    navBooks: "Books",
    navSearch: "Search",
    navChat: "Chat",
    navAdmin: "Admin",
    navMagazine: "Saat Saheli Magazine",
    navLogin: "Login",
    dropdownOptions: [
      { label: "Contact Us", value: "/contact" },
      { label: "Site Policies", value: "/policies" },
      { label: "Feedback", value: "/contact" },
    ],
    myAccount: "My Account",
    logout: "Logout",
    language: "Language",
  },

  notifications: {
    title: "Notifications",
    markAllRead: "Mark all read",
    empty: "No notifications yet",
    loading: "Loading\u2026",
    unread: "unread",
    justNow: "just now",
    minute: "m",
    hour: "h",
    day: "d",
  },

  footer: {
    about: "About",
    contactUs: "Contact Us",
    sitePolicies: "Site Policies",
    privacy: "Privacy",
    terms: "Terms",
    refunds: "Refunds",
    grievance: "Complaints & Support",
    feedback: "Feedback",
    copyright: "\u00A9 2026 SaatSaheli. All rights reserved.",
  },

  home: {
    welcomeHeading: "Welcome to Saat Saheli \u2013 A Community for Passion and Creativity!",
    searchHeading: "Search Books",
    labelBookId: "Book ID",
    labelBookName: "Book Name",
    labelAuthorName: "Author Name",
    labelStatus: "Status",
    placeholderBookId: "e.g. 1",
    placeholderTitle: "Search by title...",
    placeholderAuthor: "Search by author...",
    statusAll: "All",
    statusPublished: "Published",
    statusDraft: "Draft",
    searchButton: "Search",
    clearButton: "Clear",
    searchError: "Failed to search books. Please try again.",
    searching: "Searching...",
    resultsHeading: "Results",
    resultsCount: (n) => `${n} book${n !== 1 ? "s" : ""} found`,
    noResults: "No books match your search criteria.",
    authorPrefix: "Author: ",
    idPrefix: "ID: ",
    createdPrefix: "Created: ",
    modifiedPrefix: "Modified: ",
    viewingBook: (id) => `Viewing: Book #${id}`,
    viewLabel: (title) => `View ${title}`,
    searchAriaLabel: "Search books",
    catMagazine: "Magazine",
    catBooks: "Books",
  },

  about: {
    heading: "About SaatSaheli",
    description:
      "Do you have a hobby, skill, or passion you can't stop talking about? Whether you're an artist, musician, writer, tech enthusiast, or simply someone who loves trying new things, Saat Saheli is the perfect creative community for you to share, learn, and connect through hobby sharing.",
    tags: ["Art", "Music", "Writing", "Tech", "Creativity", "Community"],
    subtitle: "A Community for Passion and Creativity!",
    sectionTitle: "Everything You Need to Create",
    sectionDesc: "Saat Saheli provides a full creative platform with powerful tools for writing, publishing, and connecting. Explore all the features available to our members.",
    features: [
      { icon: "\uD83D\uDCD6", title: "Create & Publish Books", desc: "Write your own book with our built-in editor. Add text, images, custom layouts, and cover designs. Publish it for the community to read with a beautiful page-flip reader.", link: "/books", linkText: "Start Writing" },
      { icon: "\uD83D\uDCF0", title: "Magazine", desc: "Read and contribute to our community magazine. Rich page editor with text blocks, images, background colors, borders, and multiple font options.", link: "/magazine", linkText: "Read Magazine" },
      { icon: "\u270D\uFE0F", title: "Poems & Poetry", desc: "Share your poetry with the world. Write in English or Hindi, add images, and let others discover your creative voice.", link: "/poems", linkText: "Explore Poems" },
      { icon: "\uD83D\uDCDD", title: "Articles & Blogs", desc: "Write articles on any topic you are passionate about \u2014 technology, culture, lifestyle, health, education, or anything that inspires you. Share your blogs and personal stories.", link: "/blogs", linkText: "Read Blogs" },
      { icon: "\uD83C\uDFA8", title: "Art & Photo Galleries", desc: "Showcase your artwork, photography, sketches, paintings, or any visual creations. Create galleries and let the community appreciate your talent.", link: "/category/Art", linkText: "View Galleries" },
      { icon: "\uD83C\uDFB5", title: "Podcasts", desc: "Listen to community podcasts or share your own audio content. Built-in text-to-speech lets you listen to any book or article as a podcast.", link: "/podcasts", linkText: "Listen Now" },
      { icon: "\uD83D\uDCAC", title: "Community Chat", desc: "Connect with other members through real-time chat rooms. Discuss ideas, collaborate on projects, or just have a friendly conversation.", link: "/chat", linkText: "Join Chat" },
      { icon: "\uD83D\uDED2", title: "Marketplace", desc: "Buy and sell handmade crafts, artwork, books, and creative products. Support fellow creators by shopping from the community.", link: "/marketplace", linkText: "Browse Marketplace" },
    ],
    hobbyTitle: "Share Your Hobbies & Passions",
    hobbyDesc: "Saat Saheli is not just for writers and artists. Whatever your passion is, you can share it here. Write about your favorite hobby, teach others a new skill, or simply document your journey. Here are some ideas to get you started:",
    hobbyIdeas: ["Recipes & Cooking", "DIY & Crafts", "Gardening Tips", "Fitness & Yoga", "Travel Diaries", "Book Reviews", "Meditation & Wellness", "Fashion & Styling", "Photography Tips", "Parenting Stories", "Home Decor Ideas", "Short Stories"],
    whyTitle: "Why Saat Saheli?",
    whyItems: [
      { icon: "\uD83C\uDF0F", title: "Multilingual", desc: "Write and read in English or Hindi. Full Devanagari font support included." },
      { icon: "\uD83D\uDD12", title: "Your Content, Your Control", desc: "Save as draft, publish when ready, unpublish anytime. You own your content." },
      { icon: "\uD83D\uDC65", title: "Supportive Community", desc: "Like, comment, favorite, and share. Connect with like-minded creators through chat." },
      { icon: "\uD83D\uDCF1", title: "Works Everywhere", desc: "Fully responsive. Use on your phone, tablet, or desktop with the same great experience." },
      { icon: "\uD83C\uDFA8", title: "AI-Powered Tools", desc: "Generate cover art with AI, use text-to-speech to listen to content, and more." },
    ],
    ctaTitle: "Ready to Start Creating?",
    ctaDesc: "Join Saat Saheli today and bring your creativity to life. Sign up and start publishing in minutes.",
    ctaSignUp: "Sign Up",
    ctaExplore: "Explore Content",
  },

  contact: {
    heading: "Contact Us",
    subheading: "Questions or Comments?",
    intro:
      "Contact us today to join our creative community, create your own page, share your hobbies, and explore a world of talent and creativity with Saat Saheli!",
    labelName: "Name",
    labelEmail: "Email",
    labelMessage: "Message",
    placeholderName: "Your name",
    placeholderEmail: "Your email",
    placeholderMessage: "Write your message...",
    sendButton: "Send Message",
    successMessage: "Thank you! Your message is ready to send.",
    emailPrompt: "Or email us directly at",
    email: "avikaventures.info@gmail.com",
    mailtoSubject: (name) => `Contact from ${name}`,
    mailtoBody: (message, name, email) => `${message}\n\nFrom: ${name} (${email})`,
  },

  sitePolicies: {
    heading: "Site Policies",
    termsHeading: "Terms of Use",
    termsBody:
      "SaatSaheli is a creative community platform operated by Avika Ventures for sharing hobbies, skills, and passions. By accessing and using SaatSaheli, you agree to comply with these terms.",
    termsListIntro: "By using SaatSaheli you agree that:",
    termsList: [
      "You are at least 18 years old. SaatSaheli accounts are for adults only, and we do not knowingly collect personal data from anyone under 18; if we learn that we have, we will delete it.",
      "You will provide accurate account information and keep your login credentials secure.",
      "You will not misuse the platform, attempt to disrupt it, or access it through unauthorized means.",
      "Payments for donations, sponsorships, and subscriptions are processed securely by Stripe; you agree to Stripe's terms when you pay.",
      "Paid plans and monthly or yearly contributions renew automatically at the price shown until you cancel. You agree to this with a checkbox before paying, and we email you a confirmation. You can cancel online at any time: members via Account → Subscriptions, others via the \"Manage or cancel\" link on the Support page or in the confirmation email. Cancellation takes effect at the end of the current billing period, with no further charges.",
      "The platform is provided “as is” without warranties; to the extent permitted by law, SaatSaheli and Avika Ventures are not liable for indirect or incidental damages.",
      "These terms are governed by the laws of India, and we may update them from time to time, with the effective date shown at the bottom of this page.",
    ],
    contentHeading: "Content Creation Policy",
    contentIntro: "Users are responsible for all content they create and publish, including books, poems, art, gallery images, tech posts, DIY projects, articles, recipes, and any other creative works. All content must be:",
    contentRules: [
      "Original or properly attributed to the creator",
      "Free from hate speech, harassment, bullying, or harmful material",
      "Appropriate for a general audience",
      "Not infringing on any copyright or intellectual property rights",
      "Free from spam, misleading information, or deceptive content",
      "Not promoting illegal activities or distributing malicious content",
      "Respectful of other community members and their creative works",
    ],
    contentFooter:
      "SaatSaheli reserves the right to remove any content that violates these guidelines without prior notice. Repeated violations may result in account suspension or permanent removal.",
    contentAcceptance: "By creating any content on SaatSaheli, you confirm that you have read, understood, and agree to abide by these content guidelines.",
    privacyHeading: "Privacy Policy",
    privacyBody:
      "We respect your privacy and are transparent about how SaatSaheli collects, uses, and protects your information. This section explains what we do, who we share data with, and the rights you have over your information.",
    privacyCollectHeading: "Information We Collect",
    privacyCollectIntro: "When you use SaatSaheli we collect:",
    privacyCollectList: [
      "Account info you provide: name, email, and optional profile details such as bio, location, and profile photo.",
      "Content you create: books, articles, recipes, podcasts, galleries, comments, and the images or audio you upload.",
      "Authentication data: hashed passwords (we never store plain text), or your Google account identifier if you sign in with Google.",
      "Technical data: IP address, browser type, device, and pages visited — collected automatically and used only in aggregate to operate and improve the platform.",
    ],
    privacyUseHeading: "How We Use Your Data",
    privacyUseIntro: "We use your information only to:",
    privacyUseList: [
      "Provide and operate the platform you signed up for.",
      "Personalize your experience — your favorites, drafts, language preference, and settings.",
      "Communicate with you about your account, content, or important platform updates.",
      "Diagnose technical issues and improve the platform.",
      "Comply with legal obligations.",
    ],
    privacyProcessorsHeading: "Service Providers",
    privacyProcessorsIntro: "SaatSaheli uses these third-party services to operate. Each receives only the minimum data it needs:",
    privacyProcessors: [
      { name: "Render", desc: "Hosts the SaatSaheli backend application." },
      { name: "Neon", desc: "Stores the SaatSaheli database (your account info and content)." },
      { name: "Cloudinary", desc: "Stores and serves the images you upload." },
      { name: "Cloudflare", desc: "Delivers static assets and DNS for saatsaheli.com." },
      { name: "Google Analytics 4", desc: "Collects anonymous usage statistics (page views, session counts) so we can understand how the site is used. We do not send names, emails, or content through this." },
      { name: "Google OAuth", desc: "Used only when you choose 'Sign in with Google' to verify your Google identity." },
    ],
    privacyProcessorsNote: "We do not sell your data. We do not share it with advertisers. We do not share your personal information with anyone outside the providers listed above.",
    privacyCookiesHeading: "Cookies and Tracking",
    privacyCookiesBody:
      "We use a small number of cookies and similar storage. A session cookie keeps you signed in. Local storage holds your language preference and unsynced draft content so you don't lose work. Google Analytics sets analytics cookies to count visits anonymously. You can clear all of these from your browser settings at any time.",
    privacyDeleteHeading: "Your Rights and Data Deletion",
    privacyDeleteBody:
      "You can update or delete your profile at any time from your account settings. To delete your full account and all associated content, email us at the address below from your registered email. We will delete your data within 30 days of confirming the request, except where law requires us to retain certain records (for example, transaction logs).",
    accountHeading: "Account Policy",
    accountRules: [
      "Users must provide accurate information when creating an account",
      "Each user may maintain one account",
      "Users are responsible for maintaining the security of their account",
      "Accounts found violating site policies may be suspended or removed",
    ],
    ipHeading: "Intellectual Property",
    ipBody:
      "All content created and published on SaatSaheli remains the intellectual property of its creator. By publishing on our platform, you grant SaatSaheli a non-exclusive license to display your content within the platform. You may remove your content at any time.",
    refundHeading: "Refund & Cancellation Policy",
    refundIntro: "SaatSaheli is operated by Avika Ventures, and all payments are processed securely by Stripe. This policy explains refunds and cancellations for contributions and subscriptions made through the platform.",
    refundDonationsHeading: "Donations & Sponsorships",
    refundDonationsBody: "Contributions made through Support Us and Sponsor Us are voluntary and are generally non-refundable, as they are applied directly to running the platform and supporting our creators. If you were charged in error — for example a duplicate charge, an incorrect amount, or a transaction you did not authorize — please contact us within 14 days of the charge and we will investigate and fully refund any verified error.",
    refundRecurringHeading: "Recurring Contributions",
    refundRecurringBody: "If you set up a monthly or annual contribution, you can cancel it at any time to stop future charges. Cancelling stops further payments but does not refund contributions already made.",
    refundSubscriptionsHeading: "Subscription Plans",
    refundSubscriptionsBody: "You can cancel a paid plan (Premium or Creator) online at any time from Account → Subscriptions. Your plan benefits remain active until the end of the current billing period. We do not provide partial or prorated refunds for unused time within a billing period, and amounts already billed for the current period are non-refundable except where required by law.",
    refundMarketplaceHeading: "Sarayu Shop orders",
    refundMarketplaceBody: "Sarayu Shop is run by Avika Ventures, which is the seller, and payment is taken through Stripe. Cancellations, returns and refunds for shop orders follow the Shop Terms and the Shipping & Returns page. Pictures that creators mark \"For Sale\" in their galleries are different: those sales are arranged directly between the buyer and the creator, and SaatSaheli does not take or refund those payments.",
    refundHowHeading: "How to Request a Refund",
    refundHowBody: "Email us from your registered email address with your payment reference number (shown on your receipt as PAY-…) or your Stripe receipt, along with the date and amount of the charge. Approved refunds are issued to the original payment method through Stripe and typically appear within 5–10 business days, depending on your bank or card issuer. If you have any billing concern, please contact us before initiating a chargeback so we can resolve it quickly.",
    contactHeading: "Contact",
    contactBody: "For questions about these policies, please reach out to us at",
    contactEmail: "avikaventures.info@gmail.com",
    lastUpdated: "Last updated: October 2026",
  },

  login: {
    brand: "SAA7 SAHELI",
    tagline: "Your digital book platform",
    logInHeading: "Log In",
    createAccountHeading: "Create Account",
    labelEmail: "Email",
    labelPassword: "Password",
    labelFirstName: "First Name *",
    labelLastName: "Last Name",
    labelSignupEmail: "Email *",
    labelPhone: "Phone Number",
    labelAge: "Age *",
    errorFieldRequired: "This field is required.",
    errorFixFields: "Please fix the highlighted fields below.",
    placeholderAge: "You must be 18 or older",
    errorAgeRequired: "Please enter your age.",
    errorUnderage: "You must be 18 or older to create a SaatSaheli account.",
    googleAgeHeading: "One more step",
    googleAgeIntro: "SaatSaheli is for adults 18 and over. Please confirm your age to finish creating your account.",
    googleAgeContinue: "Continue",
    googleAgeCancel: "Cancel",
    labelSignupPassword: "Password *",
    labelConfirmPassword: "Confirm Password *",
    placeholderEmail: "Enter your email",
    placeholderPassword: "Enter your password",
    placeholderFirstName: "First name",
    placeholderLastName: "Last name",
    placeholderPhone: "(optional)",
    placeholderSignupPassword: "At least 6 characters",
    placeholderConfirmPassword: "Re-enter your password",
    logInButton: "Log In",
    loggingIn: "Logging in...",
    createAccountButton: "Create Account",
    creatingAccount: "Creating Account...",
    errorEmailPasswordRequired: "Email and password are required.",
    errorSignupRequired: "First name, email, and password are required.",
    errorPasswordMismatch: "Passwords do not match.",
    errorPasswordLength: "Password must be at least 6 characters.",
    errorLoginFailed: "Login failed. Please try again.",
    errorSignupFailed: "Signup failed. Please try again.",
    errorGoogleFailed: "Google sign-in failed. Please try again.",
    googleUnavailableInApp: "Google sign-in isn't available inside {app}'s browser.",
    googleUnavailableGeneric: "Google sign-in isn't available in this browser.",
    googleUnavailableBody: "Please sign in with your email and password below.",
    googleUnavailableBodyAndroid: "Sign in with your email and password below, or open this page in Chrome to use Google.",
    googleUnavailableBodyIos: "Sign in with your email and password below, or tap the ••• menu and choose “Open in Safari” to use Google.",
    googleUnavailableOpenChrome: "Open in Chrome",
    googleUnavailableCopyLink: "Copy link",
    googleUnavailableLinkCopied: "Link copied",
    errorInvalidEmail: "Please enter a valid email address.",
    errorInvalidPhone: "Please enter a valid phone number (at least 10 digits).",
    errorTermsRequired: "You must accept the Terms and Conditions to create an account.",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    switchToSignup: "Create Account",
    switchToLogin: "Log In",
  },

  bookManager: {
    heading: "Book Manager",
    createNewBook: "Create New Book",
    myDrafts: "My Drafts",
    allMyBooks: "All My Books",
    createHeading: "Create New Book",
    placeholderTitle: "Enter book title...",
    createButton: "Create Book",
    creating: "Creating...",
    draftsHeading: "My Drafts",
    allBooksHeading: "All My Books",
    editHeading: "Edit Book",
    previewHeading: (title) => `Preview: ${title}`,
    noDrafts: "No drafts found.",
    noBooks: "No books found.",
    saveTitle: "Save Title",
    saveAsDraft: "Save as Draft",
    publish: "Publish",
    preview: "Preview",
    deleteBook: "Delete Book",
    addNewPage: "Add New Page",
    placeholderPageNumber: "Page #",
    placeholderContent: "Page content...",
    placeholderEditContent: "Content",
    addPage: "Add Page",
    pagesCount: (n) => `Pages (${n})`,
    emptyPage: "(empty)",
    image1Label: "Image 1",
    image2Label: "Image 2",
    image1Alt: "img1",
    image2Alt: "img2",
    chooseFile: "Choose File",
    uploading: "Uploading...",
    textFormatting: "Text Formatting",
    fontSans: "Sans-serif",
    fontSerif: "Serif",
    fontMono: "Monospace",
    fontCursive: "Cursive",
    ariaFontFamily: "Font family",
    ariaFontSize: "Font size",
    ariaFontColor: "Font color",
    modified: "Modified: ",
    backToMenu: "Back to Menu",
    backToEdit: "Back to Edit",
    msgImageUploaded: "Image uploaded!",
    msgUploadFailed: (err) => `Upload failed: ${err}`,
    msgEnterTitle: "Please enter a book title",
    msgBookCreated: "Book created as Draft!",
    msgCreateFailed: (err) => `Failed to create book: ${err}`,
    msgTitleUpdated: "Title updated!",
    msgTitleFailed: "Failed to update title",
    msgPublished: "Book published!",
    msgPublishFailed: "Failed to publish",
    msgDraftSaved: "Saved as draft!",
    msgDraftFailed: "Failed to save draft",
    confirmDeleteBook: "Are you sure you want to delete this book and all its pages?",
    msgBookDeleted: "Book deleted!",
    msgDeleteBookFailed: "Failed to delete book",
    msgPageNumberRequired: "Page number is required",
    msgPageAdded: "Page added!",
    msgAddPageFailed: "Failed to add page",
    msgPageUpdated: "Page updated!",
    msgUpdatePageFailed: "Failed to update page",
    confirmDeletePage: "Delete this page?",
    msgPageDeleted: "Page deleted!",
    msgDeletePageFailed: "Failed to delete page",
    createWithAI: "Create with AI",
    generatingImage: "Generating...",
    msgImageGenerated: "AI image generated!",
    msgGenerateFailed: (err) => `AI generation failed: ${err}`,
    msgEmptyContentForAI: "Please enter a description for the AI image.",
    aiPromptDialog: "Describe the image you want to generate:",
    imageStyleLabel: "Image Style",
    imageStyles: [
      { value: "general", label: "General" },
      { value: "children", label: "Children's Book" },
      { value: "poetry", label: "Poetry" },
      { value: "story", label: "Story" },
      { value: "art", label: "Fine Art" },
      { value: "science", label: "Science" },
      { value: "politics", label: "Politics" },
      { value: "technology", label: "Technology" },
      { value: "geography", label: "Geography" },
      { value: "history", label: "History" },
      { value: "fantasy", label: "Fantasy" },
      { value: "realistic", label: "Realistic" },
    ],
    createFromDocument: "Create from Document",
    appendDocHeading: "Add pages from a document",
    appendDocHint: "Adds a PDF or Word document's pages to the END of this book — use it to build one book from several files. The new pages keep this book's page shape.",
    appendDocButton: "Add Pages from PDF",
    appendingDoc: "Adding pages\u2026",
    pageSizeLabel: "Page shape",
    pageSizeAuto: "Match my file",
    pageSizeAutoCaption: "Use the shape of the PDF you upload",
    pageSizeHint: "Your pages are fitted inside a reader page of this shape, so they are shown whole instead of being cropped to fit. It does not have to be an exact match \u2014 anything slightly off just gets a thin margin.",
    pageShapeNames: {
      PORTRAIT: "Vertical",
      TALL: "Tall",
      SQUARE: "Square",
      LANDSCAPE: "Horizontal",
    },
    pageShapeDescriptions: {
      PORTRAIT: "Standard books, magazines",
      TALL: "Novels, chapter books",
      SQUARE: "Children's books, photo books",
      LANDSCAPE: "Picture books, albums",
    },
    uploadDocHeading: "Create Book from Document",
    uploadDocHint: "Upload a PDF or Word document (.pdf, .docx, .doc). The text will be extracted and split into pages automatically.",
    uploadDocButton: "Upload & Create Book",
    uploadingDoc: "Processing document...",
    msgPagesAppended: (n) => `Added ${n} page${n === 1 ? "" : "s"} to this book.`,
    msgAppendFailed: (e) => `Could not add pages: ${e}`,
    msgBookFromDoc: (n) => `Book created with ${n} pages from document!`,
    msgDocUploadFailed: (err) => `Document upload failed: ${err}`,
  },

  searchBooks: {
    heading: "Search Books",
    placeholderBookId: "Book ID",
    placeholderTitle: "Title",
    placeholderAuthor: "Author name",
    ariaBookId: "Search by book ID",
    ariaTitle: "Search by title",
    ariaAuthor: "Search by author",
    ariaStatus: "Filter by status",
    statusAll: "All Statuses",
    statusDraft: "Draft",
    statusPublished: "Published",
    statusArchived: "Archived",
    searching: "Searching...",
    searchButton: "Search",
    clearButton: "Clear",
    resultsHeading: (n) => `Results (${n})`,
    noResults: "No books found.",
    thId: "ID",
    thTitle: "Title",
    thAuthor: "Author",
    thStatus: "Status",
    thModified: "Modified",
    emptyAuthor: "\u2014",
    booksHeading: (n) => `Books (${n})`,
    backToTable: "Back to Table",
    byAuthor: (name) => `by ${name}`,
    advancedSearch: "Advanced Search",
    simpleSearch: "Simple Search",
    categoryFilter: "Category",
    categoryAll: "All Categories",
  },

  account: {
    heading: "My Account",
    userInfoHeading: "Profile",
    labelName: "Name",
    labelEmail: "Email",
    createProfile: "Create My Profile",
    editProfile: "Edit Profile",
    viewPublicProfile: "View Public Profile",
    shareProfile: "Share My Profile",
    shareProfileCopied: "Link copied!",
    usageTitle: "Your plan & usage",
    usageGalleries: "Galleries",
    usagePictures: "Pictures",
    usageStorage: "Storage",
    usageBooks: "Books",
    usageUpgrade: "Upgrade",
    usageMarketNote: "Marking gallery items For Sale is a Premium feature.",
    saleMark: "Mark for sale",
    salePremium: "Premium",
    saleForSale: "For sale",
    saleSold: "Sold",
    saleEdit: "Edit sale details",
    saleRemove: "Remove from sale",
    saleMarkSold: "Mark as sold",
    salePricePlaceholder: "Price, e.g. ₹5,000 or $120 or Ask for price",
    saleNotePlaceholder: "Note for buyers — size, medium, shipping, how to pay",
    saleSaved: "Sale details saved.",
    saleRemoved: "Removed from sale.",
    saleFailed: "Could not save sale details.",
    saleUpgradeMsg: "Marking gallery items For Sale is a Premium feature. Upgrade to list your artwork and hear from buyers.",
    labelHeadline: "Headline",
    labelLocation: "Location",
    labelBio: "About Me",
    labelInterests: "My Interests",
    labelFields: "My Fields",
    booksHeading: "My Books",
    thTitle: "Title",
    thStatus: "Status",
    thCreated: "Created",
    thModified: "Modified",
    thActions: "Actions",
    editButton: "Edit",
    emptyState: "You haven't created any books yet.",
    loading: "Loading your books...",
    error: "Failed to load books. Please try again.",
    loginRequired: "Please log in to view your account.",
  },

  profile: {
    heading: "Create My Profile",
    editHeading: "Edit Profile",
    labelDisplayName: "Full Name / Display Name",
    labelHeadline: "Headline",
    labelProfileImage: "Profile Image",
    labelLocation: "Location",
    labelBio: "About Me / Bio",
    placeholderDisplayName: "Your display name",
    placeholderHeadline: "e.g., Passionate Weaver of Stories and Silk",
    labelHandle: "Profile URL id",
    placeholderHandle: "e.g., Chitra-Sharma",
    handleHelp: "Your public profile link. Letters, numbers and hyphens only; suggested from your name.",
    handleChecking: "Checking availability…",
    handleAvailable: "This profile link is available.",
    handleTaken: "That profile URL id is already taken — try adding a number or word.",
    handleInvalid: "Use 2–40 letters, numbers or hyphens (not only numbers).",
    placeholderLocation: "City, Country",
    placeholderBio: "Tell your story in your own words...",
    imageAlt: "Profile image",
    uploading: "Uploading...",
    imageUploaded: "Image uploaded!",
    imageUploadFailed: "Failed to upload image.",
    nameRequired: "Display name is required.",
    labelInterests: "I want to create",
    labelFields: "My Fields of Interest",
    interestOptions: ["My Page", "Book", "Gallery", "Poems", "Blog", "Article", "Recipes", "DIY", "Other"],
    fieldOptions: ["Art", "Music", "Writing", "Tech", "Creativity", "Community"],
    saveButton: "Save Profile",
    saving: "Saving...",
    saved: "Profile saved!",
    saveFailed: "Failed to save profile. Please try again.",
    backToAccount: "Back to Account",
    loading: "Loading profile...",
    loadError: "Failed to load profile.",
    loginRequired: "Please log in to edit your profile.",
  },

  publicProfile: {
    loading: "Loading profile...",
    notFound: "Profile not found.",
    memberSince: "Member since",
    booksHeading: "Published Books",
    noBooks: "No published books yet.",
  },

  publicBooks: {
    heading: "Published Books",
    loading: "Loading books...",
    error: "Failed to load books. Please try again.",
    emptyState: "No published books available yet.",
    readButton: "Read",
    backToBooks: "Back to Books",
    home: "Home",
    loginPrompt: "Log in to create and manage your own books.",
  },

  readBook: {
    backToSearch: "Back to Search",
    home: "Home",
    books: "Books",
    prevBook: "Previous Book",
    nextBook: "Next Book",
  },

  flipBook: {
    gateKicker: (n, total) => `Free preview · ${n} of ${total} pages`,
    gateTitle: "Enjoying this issue?",
    gateTextLogin: "Log in to read the rest of this issue. It's free — every member reads every issue in full.",
    gateLogin: "Log in to continue",
    gateSignup: "New here? Create a free account",
    pageHeading: (n) => `Page ${n}`,
    pageImageAlt: (n, i) => `Page ${n} - ${i}`,
    prevPage: "Previous page",
    nextPage: "Next page",
    firstPage: "First page",
    lastPage: "Last page",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    zoomReset: "Reset zoom",
    fullscreen: "Full Screen",
    exitFullscreen: "Exit Full Screen",
  },

  pageLayoutEditor: {
    labelBar: "Page Layout Editor \u2014 drag images to position, drag corner to resize",
    image1Label: "Image 1",
    image2Label: "Image 2",
  },

  category: {
    comingSoon: (title) => `${title} की सामग्री जल्द आ रही है। बने रहें!`,
    backToHome: "Back to Home",
  },

  categoryPage: {
    tabBrowse: "Browse",
    tabCreate: "Create",
    tabMyBooks: "My Books",
    noPublished: "No published books in this category yet.",
    beFirst: "Be the first to create one!",
    loginToCreate: "Log in to create content",
    createHeading: (title) => `Create a ${title} Book`,
    titlePlaceholder: "Enter book title...",
    createButton: "Create Book",
    createHint: "Your book will be created as a draft. Use the Books page to add pages, images, and publish.",
    myBooksHeading: (title) => `My ${title} Books`,
    noMyBooks: "You haven't created any books in this category yet.",
    createFirst: "Create your first one",
    enterTitle: "Please enter a title",
    bookCreated: "Book created as Draft!",
    createFailed: "Failed to create book",
    confirmDelete: "Delete this book?",
    bookDeleted: "Book deleted!",
    deleteFailed: "Failed to delete book",
    bookPublished: "Book published!",
    publishFailed: "Failed to publish",
    backToBrowse: "Back",
    descriptions: {
      art: "Explore and share Art content with the community",
      music: "Explore and share Music content with the community",
      writing: "Explore and share Writing content with the community",
      tech: "Explore and share Technology content with the community",
      creativity: "Explore and share Creativity content with the community",
      community: "Explore and share Community content with the community",
    },
  },

  techPage: {
    heading: "Tech",
    description: "Explore technology topics across multiple dimensions",
    tabTechnical: "Technical",
    tabCareer: "Career & Industry",
    tabCommunity: "Community & Representation",
    tabOther: "Other",
    descTechnical: "Programming, coding tutorials, tools, and technical resources",
    descCareer: "Tech careers, job skills, industry insights, and professional growth",
    descCommunity: "Women in tech, diversity, inclusion, and representation stories",
    descOther: "Miscellaneous tech topics, trends, and interesting finds",
    comingSoon: "Content coming soon. Stay tuned!",
  },

  admin: {
    heading: "Admin Dashboard",
    tabStats: "Stats",
    tabUsers: "Users",
    tabBooks: "Books",
    totalUsers: "Total Users",
    activeUsers: "Active Users",
    blockedUsers: "Blocked Users",
    adminCount: "Admins",
    totalBooks: "Total Books",
    publishedBooks: "Published",
    draftBooks: "Drafts",
    colName: "Name",
    colEmail: "Email",
    colRole: "Role",
    colStatus: "Status",
    colLastLogin: "Last Login",
    colActions: "Actions",
    colTitle: "Title",
    colAuthor: "Author",
    colBookStatus: "Status",
    colModified: "Modified",
    roleUpdated: "Role updated!",
    roleUpdateFailed: "Failed to update role",
    statusUpdated: "Status updated!",
    statusUpdateFailed: "Failed to update status",
    confirmDeleteBook: "Are you sure you want to delete this book?",
    bookDeleted: "Book deleted!",
    bookDeleteFailed: "Failed to delete book",
  },

  chat: {
    roomsHeading: "Chat Rooms",
    noMessages: "No messages yet. Start the conversation!",
    selectRoom: "Select a room to start chatting",
    placeholder: "Type a message...",
    sendButton: "Send",
    sending: "...",
    deleteMessage: "Delete",
  },

  messages: {
    title: "Messages",
    headerTitle: "Messages",
    roleBuyer: "Buyer",
    roleSeller: "Seller",
    emptyTitle: "No conversations yet.",
    emptyHint: "Find an artwork marked For Sale and tap Contact seller to start one.",
    browseGalleries: "Browse galleries",
    noMessagesYet: "No messages yet",
    pickThread: "Pick a conversation to read it.",
    firstHint: "Say hello and ask about the piece — size, price, shipping.",
    composePlaceholder: "Write a message…",
    send: "Send",
    sendFailed: "Could not send. Please try again.",
    loadError: "Could not load this conversation.",
    closed: "Closed",
    closedHint: "This conversation has been closed.",
    privacyNote: "Private between you and the other party. Saat Saheli admins may review conversations for safety.",
    lockedText: "Please log in to see your messages.",
    lockedCta: "Log in",
    guestBadge: "Guest",
    guestBanner: "This buyer contacted you without an account. Reply to them by email or phone — anything you type below is kept as your own note.",
    guestReplyEmail: "Reply by email",
    guestCall: "Call",
    guestNotePlaceholder: "Private note for your records…",
  },

  gallery: {
    heading: "Photo Gallery",
    forSale: "For sale",
    sold: "Sold",
    askPrice: "Ask for price",
    contactCreator: "Contact seller",
    bySeller: "by",
    guestTitle: "Contact the seller",
    guestLoginInstead: "Have an account? Log in to chat",
    guestName: "Your name",
    guestEmail: "Email address",
    guestPhone: "Phone number",
    guestMessage: "Your message — size, price, shipping…",
    guestSend: "Send to seller",
    guestNeedName: "Please enter your name.",
    guestNeedEmail: "Please enter a valid email address.",
    guestNeedPhone: "Please enter a valid phone number.",
    guestNeedMessage: "Please write a message.",
    guestNeedCaptcha: "Please complete the reCAPTCHA.",
    guestFailed: "Could not send your message. Please try again.",
    guestSentTitle: "Sent!",
    guestSentText: "The seller has your message and will reply to your email or phone.",
    guestPrivacy: "Your details are shared only with this seller (and Saat Saheli admins for safety).",
    back: "Back",
    noPhotos: "No photos in this gallery yet.",
    writeComment: "Write a comment...",
    postComment: "Post",
    noComments: "No comments yet. Be the first!",
    commentsHeading: (n) => `Comments (${n})`,
    deleteComment: "Delete",
    loginToComment: "Log in to comment",
    photos: (n) => `${n} photo${n !== 1 ? "s" : ""}`,
  },

  magazine: {
    heading: "Saat Saheli Magazine",
    loading: "Loading magazine...",
    notFound: "Magazine is not available yet.",
    pageOf: (current, total) => `Page ${current} of ${total}`,
    pickEdition: "Choose an edition to read",
    backToList: "Back to Magazines",
  },

  magazineEditor: {
    heading: "Magazine Editor",
    createMagazine: "Create Magazine",
    editPage: "Edit Page",
    addPage: "Add Page",
    deletePage: "Delete Page",
    backgroundColor: "Background Color",
    borderStyle: "Border Style",
    borderColor: "Border Color",
    borderWidth: "Border Width",
    addTextBlock: "+ Text",
    addImageBlock: "+ Image",
    textContent: "Text Content",
    fontFamily: "Font",
    fontSize: "Size",
    fontColor: "Color",
    publish: "Publish",
    saveDraft: "Save Draft",
    unpublish: "Unpublish",
    publishSuccess: "Magazine published successfully!",
    unpublishSuccess: "Magazine unpublished (back to draft)",
    saveSuccess: "Page saved successfully!",
    deleteSuccess: "Page deleted successfully!",
    draftSaved: "Draft saved!",
    pageNumber: "Page",
    emptyPage: "Empty",
    preview: "Preview",
    uploadImage: "Upload Image",
    backToEditor: "Back to Editor",
    newEdition: "+ New Edition",
    editions: "Editions",
    untitled: "Untitled",
    pages: "Pages",
    cover: "Cover",
    back: "Back",
    bg: "BG",
    border: "Border",
    none: "None",
    solid: "Solid",
    dashed: "Dashed",
    dotted: "Dotted",
    double: "Double",
    enterText: "Enter text...",
    bold: "Bold",
    italic: "Italic",
    underline: "Underline",
    left: "Left",
    center: "Center",
    right: "Right",
    remove: "Remove",
    replace: "Replace",
    fit: "Fit",
    fillCrop: "Fill (crop)",
    fitShowFull: "Fit (show full)",
    stretch: "Stretch",
    opacity: "Opacity",
    fitToPage: "Fit to Page",
    fillPage: "Fill Page",
    uploading: "Uploading...",
    importing: "Importing...",
    uploadDoc: "Upload PDF/Word",
    saving: "Saving...",
    savePage: "Save Page",
    canvasHint: "Drag to move — corner handle to resize",
    pageTextLabel: "Page Text (for search & read-aloud — optional)",
    pageTextPlaceholder: "Optional plain text content...",
    newEditionPrompt: "Enter title for new magazine edition:",
    newEditionCreated: "New edition created!",
    docImported: "Document pages imported!",
    importModePrompt: "This magazine already has pages. Replace them with the imported PDF (first page becomes the cover), or append the imported pages to the end?",
    importModeReplace: "Replace",
    importModeAppend: "Append",
    importModeCancel: "Cancel",
  },

  dropdown: {
    defaultText: "Select",
  },
};

export const hi = {
  common: {
    back: "वापस",
    share: "साझा करें",
    edit: "संपादित करें",
    delete: "हटाएं",
    save: "सहेजें",
    cancel: "रद्द करें",
    loading: "लोड हो रहा है...",
    search: "खोजें",
    clear: "साफ़ करें",
    remove: "हटाएं",
    or: "या",
    noResults: "कोई किताब नहीं मिली।",
    published: "प्रकाशित",
    draft: "ड्राफ्ट",
  },

  header: {
    skipLink: "मुख्य सामग्री पर जाएं",
    logoAlt: "सातसहेली सरस्वती लोगो",
    siteName: "सात सहेली",
    siteTagline: "सात सहेली में आपका स्वागत है \u2013 जुनून और रचनात्मकता का समुदाय!",
    navHome: "होम",
    navAbout: "हमारे बारे में",
    navBooks: "किताबें",
    navSearch: "खोजें",
    navChat: "चैट",
    navAdmin: "व्यवस्थापक",
    navMagazine: "सात सहेली पत्रिका",
    navLogin: "लॉगिन",
    dropdownOptions: [
      { label: "संपर्क करें", value: "/contact" },
      { label: "साइट नीतियां", value: "/policies" },
      { label: "प्रतिक्रिया", value: "/contact" },
    ],
    myAccount: "मेरा खाता",
    logout: "लॉगआउट",
    language: "भाषा",
  },

  notifications: {
    title: "सूचनाएं",
    markAllRead: "सभी पढ़ी हुई चिह्नित करें",
    empty: "अभी तक कोई सूचना नहीं",
    loading: "लोड हो रहा है…",
    unread: "अपठित",
    justNow: "अभी",
    minute: "मि",
    hour: "घं",
    day: "दि",
  },

  footer: {
    about: "हमारे बारे में",
    contactUs: "संपर्क करें",
    sitePolicies: "साइट नीतियां",
    privacy: "गोपनीयता",
    terms: "शर्तें",
    refunds: "रिफ़ंड",
    grievance: "शिकायत और सहायता",
    feedback: "प्रतिक्रिया",
    copyright: "\u00A9 2026 सातसहेली। सर्वाधिकार सुरक्षित।",
  },

  home: {
    welcomeHeading: "सात सहेली में आपका स्वागत है \u2013 जुनून और रचनात्मकता का समुदाय!",
    searchHeading: "किताबें खोजें",
    labelBookId: "किताब आईडी",
    labelBookName: "किताब का नाम",
    labelAuthorName: "लेखक का नाम",
    labelStatus: "स्थिति",
    placeholderBookId: "जैसे 1",
    placeholderTitle: "शीर्षक से खोजें...",
    placeholderAuthor: "लेखक से खोजें...",
    statusAll: "सभी",
    statusPublished: "प्रकाशित",
    statusDraft: "ड्राफ्ट",
    searchButton: "खोजें",
    clearButton: "साफ़ करें",
    searchError: "किताबें खोजने में विफल। कृपया पुनः प्रयास करें।",
    searching: "खोज रहे हैं...",
    resultsHeading: "परिणाम",
    resultsCount: (n) => `${n} किताब${n !== 1 ? "ें" : ""} मिली${n !== 1 ? "ं" : ""}`,
    noResults: "आपकी खोज से कोई किताब नहीं मिली।",
    authorPrefix: "लेखक: ",
    idPrefix: "आईडी: ",
    createdPrefix: "बनाई गई: ",
    modifiedPrefix: "संशोधित: ",
    viewingBook: (id) => `देख रहे हैं: किताब #${id}`,
    viewLabel: (title) => `${title} देखें`,
    searchAriaLabel: "किताबें खोजें",
    catMagazine: "पत्रिका",
    catBooks: "किताबें",
  },

  about: {
    heading: "सातसहेली के बारे में",
    description:
      "क्या आपके पास कोई शौक, कौशल या जुनून है जिसके बारे में आप बात करते नहीं थकते? चाहे आप कलाकार हों, संगीतकार, लेखक, तकनीकी उत्साही, या बस कुछ नया करने का शौक रखते हों, सात सहेली आपके लिए एकदम सही रचनात्मक समुदाय है जहां आप अपने शौक साझा कर सकते हैं, सीख सकते हैं और जुड़ सकते हैं।",
    tags: ["कला", "संगीत", "लेखन", "तकनीक", "रचनात्मकता", "समुदाय"],
    subtitle: "जुनून और रचनात्मकता का समुदाय!",
    sectionTitle: "सृजन के लिए सब कुछ उपलब्ध",
    sectionDesc: "सात सहेली लेखन, प्रकाशन और जुड़ने के लिए शक्तिशाली उपकरणों के साथ एक पूर्ण रचनात्मक मंच प्रदान करती है। हमारे सदस्यों के लिए उपलब्ध सभी सुविधाओं का अन्वेषण करें।",
    features: [
      { icon: "\uD83D\uDCD6", title: "किताबें बनाएं और प्रकाशित करें", desc: "हमारे बिल्ट-इन संपादक से अपनी किताब लिखें। टेक्स्ट, चित्र, कस्टम लेआउट और कवर डिज़ाइन जोड़ें। समुदाय के लिए सुंदर पेज-फ्लिप रीडर के साथ प्रकाशित करें।", link: "/books", linkText: "लिखना शुरू करें" },
      { icon: "\uD83D\uDCF0", title: "पत्रिका", desc: "हमारी सामुदायिक पत्रिका पढ़ें और योगदान दें। टेक्स्ट ब्लॉक, चित्र, पृष्ठभूमि रंग, बॉर्डर और कई फ़ॉन्ट विकल्पों के साथ समृद्ध पृष्ठ संपादक।", link: "/magazine", linkText: "पत्रिका पढ़ें" },
      { icon: "\u270D\uFE0F", title: "कविताएं", desc: "अपनी कविताओं को दुनिया के साथ साझा करें। हिंदी या अंग्रेज़ी में लिखें, चित्र जोड़ें, और दूसरों को अपनी रचनात्मक आवाज़ सुनने दें।", link: "/poems", linkText: "कविताएं देखें" },
      { icon: "\uD83D\uDCDD", title: "लेख और ब्लॉग", desc: "किसी भी विषय पर लेख लिखें \u2014 तकनीक, संस्कृति, जीवनशैली, स्वास्थ्य, शिक्षा, या जो भी आपको प्रेरित करे। अपने ब्लॉग और व्यक्तिगत कहानियां साझा करें।", link: "/blogs", linkText: "ब्लॉग पढ़ें" },
      { icon: "\uD83C\uDFA8", title: "कला और फोटो गैलरी", desc: "अपनी कलाकृति, फोटोग्राफी, स्केच, पेंटिंग या किसी भी दृश्य रचना को प्रदर्शित करें। गैलरी बनाएं और समुदाय को अपनी प्रतिभा की सराहना करने दें।", link: "/category/Art", linkText: "गैलरी देखें" },
      { icon: "\uD83C\uDFB5", title: "पॉडकास्ट", desc: "सामुदायिक पॉडकास्ट सुनें या अपनी ऑडियो सामग्री साझा करें। बिल्ट-इन टेक्स्ट-टू-स्पीच से किसी भी किताब या लेख को पॉडकास्ट के रूप में सुनें।", link: "/podcasts", linkText: "अभी सुनें" },
      { icon: "\uD83D\uDCAC", title: "सामुदायिक चैट", desc: "रियल-टाइम चैट रूम के माध्यम से अन्य सदस्यों से जुड़ें। विचारों पर चर्चा करें, परियोजनाओं पर सहयोग करें, या बस दोस्ताना बातचीत करें।", link: "/chat", linkText: "चैट में शामिल हों" },
      { icon: "\uD83D\uDED2", title: "मार्केटप्लेस", desc: "हस्तनिर्मित शिल्प, कलाकृति, किताबें और रचनात्मक उत्पाद खरीदें और बेचें। समुदाय से खरीदारी करके साथी रचनाकारों का समर्थन करें।", link: "/marketplace", linkText: "मार्केटप्लेस देखें" },
    ],
    hobbyTitle: "अपने शौक और जुनून साझा करें",
    hobbyDesc: "सात सहेली केवल लेखकों और कलाकारों के लिए नहीं है। आपका जुनून जो भी हो, आप इसे यहां साझा कर सकते हैं। अपने पसंदीदा शौक के बारे में लिखें, दूसरों को नया कौशल सिखाएं, या बस अपनी यात्रा का दस्तावेजीकरण करें। शुरू करने के लिए कुछ विचार:",
    hobbyIdeas: ["व्यंजन विधि और खाना बनाना", "DIY और शिल्प", "बागवानी टिप्स", "फिटनेस और योग", "यात्रा डायरी", "पुस्तक समीक्षा", "ध्यान और कल्याण", "फैशन और स्टाइलिंग", "फोटोग्राफी टिप्स", "पालन-पोषण की कहानियां", "गृह सज्जा विचार", "लघु कहानियां"],
    whyTitle: "सात सहेली क्यों?",
    whyItems: [
      { icon: "\uD83C\uDF0F", title: "बहुभाषी", desc: "हिंदी या अंग्रेज़ी में लिखें और पढ़ें। पूर्ण देवनागरी फ़ॉन्ट समर्थन शामिल।" },
      { icon: "\uD83D\uDD12", title: "आपकी सामग्री, आपका नियंत्रण", desc: "ड्राफ्ट के रूप में सहेजें, तैयार होने पर प्रकाशित करें, कभी भी अप्रकाशित करें। आपकी सामग्री आपकी है।" },
      { icon: "\uD83D\uDC65", title: "सहायक समुदाय", desc: "लाइक, कमेंट, फेवरिट और शेयर करें। चैट के माध्यम से समान विचारधारा वाले रचनाकारों से जुड़ें।" },
      { icon: "\uD83D\uDCF1", title: "हर जगह काम करता है", desc: "पूरी तरह से रिस्पॉन्सिव। अपने फोन, टैबलेट या डेस्कटॉप पर समान अनुभव।" },
      { icon: "\uD83C\uDFA8", title: "AI-संचालित उपकरण", desc: "AI से कवर आर्ट बनाएं, टेक्स्ट-टू-स्पीच से सामग्री सुनें, और बहुत कुछ।" },
    ],
    ctaTitle: "सृजन शुरू करने के लिए तैयार हैं?",
    ctaDesc: "आज ही सात सहेली से जुड़ें और अपनी रचनात्मकता को जीवन दें। साइन अप करें और मिनटों में प्रकाशन शुरू करें।",
    ctaSignUp: "साइन अप करें",
    ctaExplore: "सामग्री देखें",
  },

  contact: {
    heading: "संपर्क करें",
    subheading: "प्रश्न या टिप्पणियां?",
    intro:
      "हमारे रचनात्मक समुदाय से जुड़ने, अपना पेज बनाने, अपने शौक साझा करने और सात सहेली के साथ प्रतिभा और रचनात्मकता की दुनिया का पता लगाने के लिए आज ही संपर्क करें!",
    labelName: "नाम",
    labelEmail: "ईमेल",
    labelMessage: "संदेश",
    placeholderName: "आपका नाम",
    placeholderEmail: "आपका ईमेल",
    placeholderMessage: "अपना संदेश लिखें...",
    sendButton: "संदेश भेजें",
    successMessage: "धन्यवाद! आपका संदेश भेजने के लिए तैयार है।",
    emailPrompt: "या हमें सीधे ईमेल करें",
    email: "avikaventures.info@gmail.com",
    mailtoSubject: (name) => `${name} से संपर्क`,
    mailtoBody: (message, name, email) => `${message}\n\nसे: ${name} (${email})`,
  },

  sitePolicies: {
    heading: "साइट नीतियां",
    termsHeading: "उपयोग की शर्तें",
    termsBody:
      "सातसहेली अविका वेंचर्स द्वारा संचालित एक रचनात्मक समुदाय मंच है, जो शौक, कौशल और जुनून साझा करने के लिए है। सातसहेली का उपयोग करके, आप इन शर्तों का पालन करने के लिए सहमत हैं।",
    termsListIntro: "सातसहेली का उपयोग करके आप सहमत होते हैं कि:",
    termsList: [
      "आपकी आयु कम से कम 18 वर्ष है। सातसहेली खाते केवल वयस्कों के लिए हैं, और हम जानबूझकर 18 वर्ष से कम आयु के किसी व्यक्ति का व्यक्तिगत डेटा एकत्र नहीं करते; यदि हमें पता चलता है कि ऐसा हुआ है, तो हम उसे हटा देंगे।",
      "आप सटीक खाता जानकारी देंगे और अपने लॉगिन विवरण सुरक्षित रखेंगे।",
      "आप मंच का दुरुपयोग नहीं करेंगे, उसे बाधित करने का प्रयास नहीं करेंगे, या अनधिकृत तरीकों से उस तक पहुँच नहीं बनाएँगे।",
      "दान, प्रायोजन और सदस्यता के भुगतान Stripe द्वारा सुरक्षित रूप से संसाधित किए जाते हैं; भुगतान करते समय आप Stripe की शर्तों से सहमत होते हैं।",
      "सशुल्क प्लान और मासिक या वार्षिक योगदान, रद्द किए जाने तक दिखाई गई कीमत पर स्वतः नवीनीकृत होते हैं। भुगतान से पहले आप एक चेकबॉक्स द्वारा इसकी सहमति देते हैं, और हम आपको पुष्टि ईमेल भेजते हैं। आप इन्हें कभी भी ऑनलाइन रद्द कर सकते हैं: सदस्य खाता → सदस्यताएँ से, अन्य लोग सहायता पृष्ठ या पुष्टि ईमेल में दिए गए \"प्रबंधित करें या रद्द करें\" लिंक से। रद्दीकरण वर्तमान बिलिंग अवधि के अंत में लागू होता है, उसके बाद कोई शुल्क नहीं लिया जाता।",
      "मंच “जैसा है” के आधार पर बिना किसी वारंटी के प्रदान किया जाता है; कानून द्वारा अनुमत सीमा तक, सातसहेली और अविका वेंचर्स अप्रत्यक्ष या आकस्मिक क्षति के लिए उत्तरदायी नहीं हैं।",
      "ये शर्तें भारत के कानूनों द्वारा शासित हैं, और हम इन्हें समय-समय पर अद्यतन कर सकते हैं, जिनकी प्रभावी तिथि इस पृष्ठ के नीचे दिखाई गई है।",
    ],
    contentHeading: "सामग्री नीति",
    contentIntro: "उपयोगकर्ता अपनी प्रकाशित सामग्री के लिए जिम्मेदार हैं। सामग्री होनी चाहिए:",
    contentRules: [
      "मूल या रचनाकार को उचित रूप से श्रेय दी गई",
      "घृणा भाषण, उत्पीड़न या हानिकारक सामग्री से मुक्त",
      "सामान्य दर्शकों के लिए उपयुक्त",
      "किसी भी कॉपीराइट या बौद्धिक संपदा अधिकारों का उल्लंघन नहीं करती",
    ],
    contentFooter:
      "सातसहेली इन दिशानिर्देशों का उल्लंघन करने वाली किसी भी सामग्री को हटाने का अधिकार रखती है।",
    privacyHeading: "गोपनीयता नीति",
    privacyBody:
      "हम आपकी गोपनीयता का सम्मान करते हैं और सातसहेली आपकी जानकारी कैसे एकत्र, उपयोग और सुरक्षित करती है, इसके बारे में पारदर्शी हैं। यह खंड बताता है कि हम क्या करते हैं, हम किसके साथ डेटा साझा करते हैं, और आपकी जानकारी पर आपके अधिकार क्या हैं।",
    privacyCollectHeading: "हम कौन सी जानकारी एकत्र करते हैं",
    privacyCollectIntro: "जब आप सातसहेली का उपयोग करते हैं तो हम एकत्र करते हैं:",
    privacyCollectList: [
      "आपके द्वारा प्रदान की गई खाता जानकारी: नाम, ईमेल, और वैकल्पिक प्रोफ़ाइल विवरण जैसे बायो, स्थान और प्रोफ़ाइल फोटो।",
      "आपके द्वारा बनाई गई सामग्री: किताबें, लेख, व्यंजन, पॉडकास्ट, गैलरियाँ, टिप्पणियाँ, और आपके द्वारा अपलोड की गई छवियाँ या ऑडियो।",
      "प्रमाणीकरण डेटा: हैश किए गए पासवर्ड (हम कभी भी प्लेन टेक्स्ट संग्रहीत नहीं करते), या यदि आप Google से साइन इन करते हैं तो आपकी Google खाता पहचान।",
      "तकनीकी डेटा: IP पता, ब्राउज़र प्रकार, डिवाइस, और देखे गए पृष्ठ — स्वचालित रूप से एकत्र किए जाते हैं और केवल समग्र रूप में मंच के संचालन और सुधार के लिए उपयोग किए जाते हैं।",
    ],
    privacyUseHeading: "हम आपके डेटा का उपयोग कैसे करते हैं",
    privacyUseIntro: "हम आपकी जानकारी का उपयोग केवल इसके लिए करते हैं:",
    privacyUseList: [
      "आपके द्वारा साइन अप किए गए मंच को प्रदान करना और संचालित करना।",
      "आपके अनुभव को व्यक्तिगत बनाना — आपके पसंदीदा, ड्राफ्ट, भाषा वरीयता और सेटिंग्स।",
      "आपके खाते, सामग्री या महत्वपूर्ण मंच अपडेट के बारे में आपसे संवाद करना।",
      "तकनीकी समस्याओं का निदान करना और मंच में सुधार करना।",
      "कानूनी दायित्वों का पालन करना।",
    ],
    privacyProcessorsHeading: "सेवा प्रदाता",
    privacyProcessorsIntro: "सातसहेली संचालन के लिए इन तृतीय-पक्ष सेवाओं का उपयोग करती है। प्रत्येक को केवल वह न्यूनतम डेटा प्राप्त होता है जिसकी उसे आवश्यकता होती है:",
    privacyProcessors: [
      { name: "Render", desc: "सातसहेली बैकएंड एप्लिकेशन को होस्ट करता है।" },
      { name: "Neon", desc: "सातसहेली डेटाबेस संग्रहीत करता है (आपकी खाता जानकारी और सामग्री)।" },
      { name: "Cloudinary", desc: "आपके द्वारा अपलोड की गई छवियों को संग्रहीत और वितरित करता है।" },
      { name: "Cloudflare", desc: "saatsaheli.com के लिए स्थैतिक संपत्तियाँ और DNS वितरित करता है।" },
      { name: "Google Analytics 4", desc: "गुमनाम उपयोग आँकड़े (पृष्ठ दृश्य, सत्र संख्या) एकत्र करता है ताकि हम समझ सकें कि साइट का उपयोग कैसे किया जाता है। हम इसके माध्यम से नाम, ईमेल या सामग्री नहीं भेजते।" },
      { name: "Google OAuth", desc: "केवल तब उपयोग किया जाता है जब आप 'Google से साइन इन करें' चुनते हैं ताकि आपकी Google पहचान सत्यापित हो सके।" },
    ],
    privacyProcessorsNote: "हम आपका डेटा नहीं बेचते। हम इसे विज्ञापनदाताओं के साथ साझा नहीं करते। हम आपकी व्यक्तिगत जानकारी ऊपर सूचीबद्ध प्रदाताओं के अलावा किसी के साथ साझा नहीं करते।",
    privacyCookiesHeading: "कुकीज़ और ट्रैकिंग",
    privacyCookiesBody:
      "हम कुछ कुकीज़ और समान संग्रहण का उपयोग करते हैं। एक सत्र कुकी आपको साइन इन रखती है। स्थानीय भंडारण आपकी भाषा वरीयता और बिना सिंक की गई ड्राफ्ट सामग्री रखता है ताकि आप अपना काम न खोएं। Google Analytics गुमनाम रूप से दौरों की गिनती करने के लिए विश्लेषण कुकीज़ सेट करता है। आप किसी भी समय अपनी ब्राउज़र सेटिंग्स से इन सभी को साफ़ कर सकते हैं।",
    privacyDeleteHeading: "आपके अधिकार और डेटा हटाना",
    privacyDeleteBody:
      "आप किसी भी समय अपनी खाता सेटिंग्स से अपनी प्रोफ़ाइल अपडेट या हटा सकते हैं। अपने पूरे खाते और सभी संबंधित सामग्री को हटाने के लिए, अपने पंजीकृत ईमेल से नीचे दिए गए पते पर हमें ईमेल करें। हम अनुरोध की पुष्टि के 30 दिनों के भीतर आपका डेटा हटा देंगे, सिवाय इसके कि कानून हमें कुछ रिकॉर्ड (उदाहरण के लिए, लेन-देन लॉग) रखने की आवश्यकता हो।",
    accountHeading: "खाता नीति",
    accountRules: [
      "खाता बनाते समय उपयोगकर्ताओं को सही जानकारी देनी होगी",
      "प्रत्येक उपयोगकर्ता एक खाता रख सकता है",
      "उपयोगकर्ता अपने खाते की सुरक्षा बनाए रखने के लिए जिम्मेदार हैं",
      "साइट नीतियों का उल्लंघन करने वाले खातों को निलंबित या हटाया जा सकता है",
    ],
    ipHeading: "बौद्धिक संपदा",
    ipBody:
      "सातसहेली पर बनाई और प्रकाशित सभी सामग्री उसके रचनाकार की बौद्धिक संपदा बनी रहती है। हमारे मंच पर प्रकाशित करके, आप सातसहेली को मंच के भीतर आपकी सामग्री प्रदर्शित करने का गैर-अनन्य लाइसेंस प्रदान करते हैं। आप किसी भी समय अपनी सामग्री हटा सकते हैं।",
    refundHeading: "धनवापसी एवं रद्दीकरण नीति",
    refundIntro: "सातसहेली का संचालन अविका वेंचर्स द्वारा किया जाता है, और सभी भुगतान Stripe द्वारा सुरक्षित रूप से संसाधित किए जाते हैं। यह नीति मंच के माध्यम से किए गए योगदान और सदस्यताओं के लिए धनवापसी और रद्दीकरण की व्याख्या करती है।",
    refundDonationsHeading: "दान एवं प्रायोजन",
    refundDonationsBody: "Support Us और Sponsor Us के माध्यम से किए गए योगदान स्वैच्छिक हैं और सामान्यतः वापस नहीं किए जाते, क्योंकि इनका उपयोग सीधे मंच को चलाने और हमारे रचनाकारों के समर्थन में किया जाता है। यदि आपसे गलती से शुल्क लिया गया है — उदाहरण के लिए दोहरा शुल्क, गलत राशि, या ऐसा लेन-देन जिसे आपने अधिकृत नहीं किया — तो कृपया शुल्क के 14 दिनों के भीतर हमसे संपर्क करें और हम जाँच कर किसी भी सत्यापित त्रुटि की पूर्ण धनवापसी करेंगे।",
    refundRecurringHeading: "आवर्ती योगदान",
    refundRecurringBody: "यदि आपने मासिक या वार्षिक योगदान सेट किया है, तो आप भविष्य के शुल्क रोकने के लिए इसे कभी भी रद्द कर सकते हैं। रद्द करने से आगे के भुगतान रुक जाते हैं, परंतु पहले किए गए योगदान वापस नहीं किए जाते।",
    refundSubscriptionsHeading: "सदस्यता योजनाएँ",
    refundSubscriptionsBody: "आप अपनी खाता सेटिंग्स से किसी भी सशुल्क योजना (Premium या Creator) को कभी भी रद्द कर सकते हैं। आपकी योजना के लाभ वर्तमान बिलिंग अवधि के अंत तक सक्रिय रहते हैं। हम बिलिंग अवधि के भीतर अप्रयुक्त समय के लिए आंशिक या आनुपातिक धनवापसी प्रदान नहीं करते, और वर्तमान अवधि के लिए पहले से लिए गए शुल्क कानून द्वारा आवश्यक होने को छोड़कर वापस नहीं किए जाते।",
    refundMarketplaceHeading: "सरयू शॉप के ऑर्डर",
    refundMarketplaceBody: "सरयू शॉप अविका वेंचर्स द्वारा चलाई जाती है, जो विक्रेता है, और भुगतान Stripe के माध्यम से लिया जाता है। शॉप के ऑर्डर का रद्दीकरण, वापसी और धनवापसी शॉप की शर्तों और शिपिंग व वापसी पृष्ठ के अनुसार होती है। रचनाकारों द्वारा अपनी गैलरी में \"बिक्री के लिए\" चिह्नित चित्र अलग हैं: वे बिक्री सीधे खरीदार और रचनाकार के बीच तय होती है, और सातसहेली उन भुगतानों को न लेती है न लौटाती है।",
    refundHowHeading: "धनवापसी का अनुरोध कैसे करें",
    refundHowBody: "अपने पंजीकृत ईमेल पते से हमें अपना भुगतान संदर्भ संख्या (आपकी रसीद पर PAY-… के रूप में दिखाई गई) या अपनी Stripe रसीद, शुल्क की तिथि और राशि सहित ईमेल करें। स्वीकृत धनवापसी Stripe के माध्यम से मूल भुगतान विधि में जारी की जाती है और आपके बैंक या कार्ड जारीकर्ता के आधार पर सामान्यतः 5–10 कार्यदिवसों में दिखाई देती है। किसी भी बिलिंग संबंधी चिंता के लिए, कृपया चार्जबैक शुरू करने से पहले हमसे संपर्क करें ताकि हम इसे शीघ्र हल कर सकें।",
    contactHeading: "संपर्क",
    contactBody: "इन नीतियों के बारे में प्रश्नों के लिए, कृपया हमसे संपर्क करें",
    contactEmail: "avikaventures.info@gmail.com",
    lastUpdated: "अंतिम अपडेट: अक्टूबर 2026",
  },

  login: {
    brand: "सात सहेली",
    tagline: "आपका डिजिटल किताब मंच",
    logInHeading: "लॉग इन",
    createAccountHeading: "खाता बनाएं",
    labelEmail: "ईमेल",
    labelPassword: "पासवर्ड",
    labelFirstName: "पहला नाम *",
    labelLastName: "उपनाम",
    labelSignupEmail: "ईमेल *",
    labelPhone: "फोन नंबर",
    labelAge: "आयु *",
    errorFieldRequired: "यह फ़ील्ड आवश्यक है।",
    errorFixFields: "कृपया नीचे हाइलाइट किए गए फ़ील्ड ठीक करें।",
    placeholderAge: "आपकी आयु 18 वर्ष या अधिक होनी चाहिए",
    errorAgeRequired: "कृपया अपनी आयु दर्ज करें।",
    errorUnderage: "सातसहेली खाता बनाने के लिए आपकी आयु 18 वर्ष या अधिक होनी चाहिए।",
    googleAgeHeading: "बस एक और कदम",
    googleAgeIntro: "सातसहेली 18 वर्ष और उससे अधिक आयु के वयस्कों के लिए है। खाता बनाना पूरा करने के लिए कृपया अपनी आयु की पुष्टि करें।",
    googleAgeContinue: "आगे बढ़ें",
    googleAgeCancel: "रद्द करें",
    labelSignupPassword: "पासवर्ड *",
    labelConfirmPassword: "पासवर्ड की पुष्टि करें *",
    placeholderEmail: "अपना ईमेल दर्ज करें",
    placeholderPassword: "अपना पासवर्ड दर्ज करें",
    placeholderFirstName: "पहला नाम",
    placeholderLastName: "उपनाम",
    placeholderPhone: "(वैकल्पिक)",
    placeholderSignupPassword: "कम से कम 6 अक्षर",
    placeholderConfirmPassword: "पासवर्ड फिर से दर्ज करें",
    logInButton: "लॉग इन",
    loggingIn: "लॉग इन हो रहा है...",
    createAccountButton: "खाता बनाएं",
    creatingAccount: "खाता बन रहा है...",
    errorEmailPasswordRequired: "ईमेल और पासवर्ड आवश्यक हैं।",
    errorSignupRequired: "पहला नाम, ईमेल और पासवर्ड आवश्यक हैं।",
    errorPasswordMismatch: "पासवर्ड मेल नहीं खाते।",
    errorPasswordLength: "पासवर्ड कम से कम 6 अक्षरों का होना चाहिए।",
    errorLoginFailed: "लॉगिन विफल। कृपया पुनः प्रयास करें।",
    errorSignupFailed: "साइनअप विफल। कृपया पुनः प्रयास करें।",
    errorGoogleFailed: "Google साइन-इन विफल। कृपया पुनः प्रयास करें।",
    googleUnavailableInApp: "{app} के ब्राउज़र में Google साइन-इन उपलब्ध नहीं है।",
    googleUnavailableGeneric: "इस ब्राउज़र में Google साइन-इन उपलब्ध नहीं है।",
    googleUnavailableBody: "कृपया नीचे अपने ईमेल और पासवर्ड से साइन इन करें।",
    googleUnavailableBodyAndroid: "नीचे अपने ईमेल और पासवर्ड से साइन इन करें, या Google उपयोग करने के लिए इस पेज को Chrome में खोलें।",
    googleUnavailableBodyIos: "नीचे अपने ईमेल और पासवर्ड से साइन इन करें, या Google उपयोग करने के लिए ••• मेन्यू से “Open in Safari” चुनें।",
    googleUnavailableOpenChrome: "Chrome में खोलें",
    googleUnavailableCopyLink: "लिंक कॉपी करें",
    googleUnavailableLinkCopied: "लिंक कॉपी हो गया",
    errorInvalidEmail: "कृपया एक वैध ईमेल पता दर्ज करें।",
    errorInvalidPhone: "कृपया एक वैध फोन नंबर दर्ज करें (कम से कम 10 अंक)।",
    errorTermsRequired: "खाता बनाने के लिए आपको नियम और शर्तें स्वीकार करनी होंगी।",
    noAccount: "खाता नहीं है?",
    hasAccount: "पहले से खाता है?",
    switchToSignup: "खाता बनाएं",
    switchToLogin: "लॉग इन",
  },

  bookManager: {
    heading: "किताब प्रबंधक",
    createNewBook: "नई किताब बनाएं",
    myDrafts: "मेरे ड्राफ्ट",
    allMyBooks: "मेरी सभी किताबें",
    createHeading: "नई किताब बनाएं",
    placeholderTitle: "किताब का शीर्षक दर्ज करें...",
    createButton: "किताब बनाएं",
    creating: "बना रहे हैं...",
    draftsHeading: "मेरे ड्राफ्ट",
    allBooksHeading: "मेरी सभी किताबें",
    editHeading: "किताब संपादित करें",
    previewHeading: (title) => `पूर्वावलोकन: ${title}`,
    noDrafts: "कोई ड्राफ्ट नहीं मिला।",
    noBooks: "कोई किताब नहीं मिली।",
    saveTitle: "शीर्षक सहेजें",
    saveAsDraft: "ड्राफ्ट के रूप में सहेजें",
    publish: "प्रकाशित करें",
    preview: "पूर्वावलोकन",
    deleteBook: "किताब हटाएं",
    addNewPage: "नया पृष्ठ जोड़ें",
    placeholderPageNumber: "पृष्ठ #",
    placeholderContent: "पृष्ठ सामग्री...",
    placeholderEditContent: "सामग्री",
    addPage: "पृष्ठ जोड़ें",
    pagesCount: (n) => `पृष्ठ (${n})`,
    emptyPage: "(खाली)",
    image1Label: "चित्र 1",
    image2Label: "चित्र 2",
    image1Alt: "चित्र1",
    image2Alt: "चित्र2",
    chooseFile: "फ़ाइल चुनें",
    uploading: "अपलोड हो रहा है...",
    textFormatting: "टेक्स्ट फ़ॉर्मेटिंग",
    fontSans: "सैन्स-सेरिफ",
    fontSerif: "सेरिफ",
    fontMono: "मोनोस्पेस",
    fontCursive: "कर्सिव",
    ariaFontFamily: "फ़ॉन्ट परिवार",
    ariaFontSize: "फ़ॉन्ट आकार",
    ariaFontColor: "फ़ॉन्ट रंग",
    modified: "संशोधित: ",
    backToMenu: "मेनू पर वापस",
    backToEdit: "संपादन पर वापस",
    msgImageUploaded: "चित्र अपलोड हो गया!",
    msgUploadFailed: (err) => `अपलोड विफल: ${err}`,
    msgEnterTitle: "कृपया किताब का शीर्षक दर्ज करें",
    msgBookCreated: "किताब ड्राफ्ट के रूप में बनाई गई!",
    msgCreateFailed: (err) => `किताब बनाने में विफल: ${err}`,
    msgTitleUpdated: "शीर्षक अपडेट हो गया!",
    msgTitleFailed: "शीर्षक अपडेट करने में विफल",
    msgPublished: "किताब प्रकाशित हो गई!",
    msgPublishFailed: "प्रकाशित करने में विफल",
    msgDraftSaved: "ड्राफ्ट सहेजा गया!",
    msgDraftFailed: "ड्राफ्ट सहेजने में विफल",
    confirmDeleteBook: "क्या आप वाकई इस किताब और उसके सभी पृष्ठों को हटाना चाहते हैं?",
    msgBookDeleted: "किताब हटा दी गई!",
    msgDeleteBookFailed: "किताब हटाने में विफल",
    msgPageNumberRequired: "पृष्ठ संख्या आवश्यक है",
    msgPageAdded: "पृष्ठ जोड़ा गया!",
    msgAddPageFailed: "पृष्ठ जोड़ने में विफल",
    msgPageUpdated: "पृष्ठ अपडेट हो गया!",
    msgUpdatePageFailed: "पृष्ठ अपडेट करने में विफल",
    confirmDeletePage: "यह पृष्ठ हटाएं?",
    msgPageDeleted: "पृष्ठ हटा दिया गया!",
    msgDeletePageFailed: "पृष्ठ हटाने में विफल",
    createWithAI: "AI से बनाएं",
    generatingImage: "बना रहे हैं...",
    msgImageGenerated: "AI चित्र बन गया!",
    msgGenerateFailed: (err) => `AI चित्र बनाने में विफल: ${err}`,
    msgEmptyContentForAI: "कृपया AI चित्र के लिए विवरण दर्ज करें।",
    aiPromptDialog: "जो चित्र आप बनाना चाहते हैं उसका वर्णन करें:",
    imageStyleLabel: "चित्र शैली",
    imageStyles: [
      { value: "general", label: "सामान्य" },
      { value: "children", label: "बच्चों की किताब" },
      { value: "poetry", label: "कविता" },
      { value: "story", label: "कहानी" },
      { value: "art", label: "ललित कला" },
      { value: "science", label: "विज्ञान" },
      { value: "politics", label: "राजनीति" },
      { value: "technology", label: "प्रौद्योगिकी" },
      { value: "geography", label: "भूगोल" },
      { value: "history", label: "इतिहास" },
      { value: "fantasy", label: "काल्पनिक" },
      { value: "realistic", label: "यथार्थवादी" },
    ],
    createFromDocument: "दस्तावेज़ से बनाएं",
    appendDocHeading: "\u0926\u0938\u094d\u0924\u093e\u0935\u0947\u091c\u093c \u0938\u0947 \u092a\u0943\u0937\u094d\u0920 \u091c\u094b\u0921\u093c\u0947\u0902",
    appendDocHint: "PDF \u092f\u093e Word \u0926\u0938\u094d\u0924\u093e\u0935\u0947\u091c\u093c \u0915\u0947 \u092a\u0943\u0937\u094d\u0920 \u0907\u0938 \u0915\u093f\u0924\u093e\u092c \u0915\u0947 \u0905\u0902\u0924 \u092e\u0947\u0902 \u091c\u0941\u0921\u093c\u0947\u0902\u0917\u0947 \u2014 \u0915\u0908 \u092b\u093c\u093e\u0907\u0932\u094b\u0902 \u0938\u0947 \u090f\u0915 \u0915\u093f\u0924\u093e\u092c \u092c\u0928\u093e\u0928\u0947 \u0915\u0947 \u0932\u093f\u090f\u0964 \u0928\u090f \u092a\u0943\u0937\u094d\u0920 \u0907\u0938\u0940 \u0915\u093f\u0924\u093e\u092c \u0915\u093e \u0906\u0915\u093e\u0930 \u0930\u0916\u0947\u0902\u0917\u0947\u0964",
    appendDocButton: "PDF \u0938\u0947 \u092a\u0943\u0937\u094d\u0920 \u091c\u094b\u0921\u093c\u0947\u0902",
    appendingDoc: "\u092a\u0943\u0937\u094d\u0920 \u091c\u094b\u0921\u093c\u0947 \u091c\u093e \u0930\u0939\u0947 \u0939\u0948\u0902\u2026",
    pageSizeLabel: "\u092a\u0943\u0937\u094d\u0920 \u0915\u093e \u0906\u0915\u093e\u0930",
    pageSizeAuto: "\u092e\u0947\u0930\u0940 \u092b\u093c\u093e\u0907\u0932 \u0915\u0947 \u0905\u0928\u0941\u0938\u093e\u0930",
    pageSizeAutoCaption: "\u0906\u092a\u0915\u0940 PDF \u0915\u093e \u0906\u0915\u093e\u0930 \u0932\u093f\u092f\u093e \u091c\u093e\u090f\u0917\u093e",
    pageSizeHint: "\u0906\u092a\u0915\u0947 \u092a\u0943\u0937\u094d\u0920 \u0907\u0938\u0940 \u0906\u0915\u093e\u0930 \u0915\u0947 \u092a\u0943\u0937\u094d\u0920 \u092e\u0947\u0902 \u092a\u0942\u0930\u0947 \u0938\u092e\u093e\u090f \u091c\u093e\u0924\u0947 \u0939\u0948\u0902, \u0915\u091f\u0924\u0947 \u0928\u0939\u0940\u0902\u0964 \u092c\u093f\u0932\u094d\u0915\u0941\u0932 \u0938\u091f\u0940\u0915 \u0939\u094b\u0928\u093e \u091c\u093c\u0930\u0942\u0930\u0940 \u0928\u0939\u0940\u0902 \u2014 \u0925\u094b\u0921\u093c\u093e \u0905\u0902\u0924\u0930 \u0939\u094b\u0928\u0947 \u092a\u0930 \u092c\u0938 \u092a\u0924\u0932\u093e \u0939\u093e\u0936\u093f\u092f\u093e \u0926\u093f\u0916\u0947\u0917\u093e\u0964",
    pageShapeNames: {
      PORTRAIT: "\u0916\u0921\u093c\u093e",
      TALL: "\u0932\u0902\u092c\u093e",
      SQUARE: "\u0935\u0930\u094d\u0917",
      LANDSCAPE: "\u0906\u0921\u093c\u093e",
    },
    pageShapeDescriptions: {
      PORTRAIT: "\u0938\u093e\u092e\u093e\u0928\u094d\u092f \u0915\u093f\u0924\u093e\u092c\u0947\u0902, \u092a\u0924\u094d\u0930\u093f\u0915\u093e\u090f\u0902",
      TALL: "\u0909\u092a\u0928\u094d\u092f\u093e\u0938, \u0915\u0939\u093e\u0928\u0940 \u0915\u093f\u0924\u093e\u092c\u0947\u0902",
      SQUARE: "\u092c\u093e\u0932 \u092a\u0941\u0938\u094d\u0924\u0915\u0947\u0902, \u092b\u094b\u091f\u094b \u092c\u0941\u0915",
      LANDSCAPE: "\u091a\u093f\u0924\u094d\u0930 \u092a\u0941\u0938\u094d\u0924\u0915\u0947\u0902, \u0910\u0932\u092c\u092e",
    },
    uploadDocHeading: "दस्तावेज़ से किताब बनाएं",
    uploadDocHint: "PDF या Word दस्तावेज़ (.pdf, .docx, .doc) अपलोड करें। टेक्स्ट स्वचालित रूप से निकाला जाएगा और पृष्ठों में विभाजित किया जाएगा।",
    uploadDocButton: "अपलोड करें और किताब बनाएं",
    uploadingDoc: "दस्तावेज़ प्रोसेस हो रहा है...",
    msgPagesAppended: (n) => `\u0907\u0938 \u0915\u093f\u0924\u093e\u092c \u092e\u0947\u0902 ${n} \u092a\u0943\u0937\u094d\u0920 \u091c\u094b\u0921\u093c\u0947 \u0917\u090f\u0964`,
    msgAppendFailed: (e) => `\u092a\u0943\u0937\u094d\u0920 \u0928\u0939\u0940\u0902 \u091c\u094b\u0921\u093c\u0947 \u091c\u093e \u0938\u0915\u0947: ${e}`,
    msgBookFromDoc: (n) => `दस्तावेज़ से ${n} पृष्ठों वाली किताब बनाई गई!`,
    msgDocUploadFailed: (err) => `दस्तावेज़ अपलोड विफल: ${err}`,
  },

  searchBooks: {
    heading: "किताबें खोजें",
    placeholderBookId: "किताब आईडी",
    placeholderTitle: "शीर्षक",
    placeholderAuthor: "लेखक का नाम",
    ariaBookId: "किताब आईडी से खोजें",
    ariaTitle: "शीर्षक से खोजें",
    ariaAuthor: "लेखक से खोजें",
    ariaStatus: "स्थिति से फ़िल्टर करें",
    statusAll: "सभी स्थितियां",
    statusDraft: "ड्राफ्ट",
    statusPublished: "प्रकाशित",
    statusArchived: "संग्रहीत",
    searching: "खोज रहे हैं...",
    searchButton: "खोजें",
    clearButton: "साफ़ करें",
    resultsHeading: (n) => `परिणाम (${n})`,
    noResults: "कोई किताब नहीं मिली।",
    thId: "आईडी",
    thTitle: "शीर्षक",
    thAuthor: "लेखक",
    thStatus: "स्थिति",
    thModified: "संशोधित",
    emptyAuthor: "\u2014",
    booksHeading: (n) => `किताबें (${n})`,
    backToTable: "तालिका पर वापस",
    byAuthor: (name) => `लेखक: ${name}`,
    advancedSearch: "उन्नत खोज",
    simpleSearch: "सामान्य खोज",
    categoryFilter: "श्रेणी",
    categoryAll: "सभी श्रेणियां",
  },

  account: {
    heading: "मेरा खाता",
    userInfoHeading: "प्रोफ़ाइल",
    labelName: "नाम",
    labelEmail: "ईमेल",
    createProfile: "मेरी प्रोफ़ाइल बनाएं",
    editProfile: "प्रोफ़ाइल संपादित करें",
    viewPublicProfile: "सार्वजनिक प्रोफ़ाइल देखें",
    shareProfile: "मेरी प्रोफ़ाइल साझा करें",
    shareProfileCopied: "लिंक कॉपी हो गया!",
    usageTitle: "आपकी योजना और उपयोग",
    usageGalleries: "गैलरी",
    usagePictures: "चित्र",
    usageStorage: "स्टोरेज",
    usageBooks: "किताबें",
    usageUpgrade: "अपग्रेड करें",
    usageMarketNote: "गैलरी आइटम को बिक्री के लिए चिह्नित करना एक प्रीमियम सुविधा है।",
    saleMark: "बिक्री के लिए चिह्नित करें",
    salePremium: "प्रीमियम",
    saleForSale: "बिक्री के लिए",
    saleSold: "बिक गया",
    saleEdit: "बिक्री विवरण संपादित करें",
    saleRemove: "बिक्री से हटाएँ",
    saleMarkSold: "बिका हुआ चिह्नित करें",
    salePricePlaceholder: "मूल्य, जैसे ₹5,000 या $120 या मूल्य पूछें",
    saleNotePlaceholder: "खरीदारों के लिए नोट — आकार, माध्यम, शिपिंग, भुगतान कैसे करें",
    saleSaved: "बिक्री विवरण सहेजा गया।",
    saleRemoved: "बिक्री से हटा दिया गया।",
    saleFailed: "बिक्री विवरण सहेजा नहीं जा सका।",
    saleUpgradeMsg: "गैलरी आइटम को बिक्री के लिए चिह्नित करना एक प्रीमियम सुविधा है। अपनी कलाकृति सूचीबद्ध करने और खरीदारों से चैट करने के लिए अपग्रेड करें।",
    labelHeadline: "शीर्षक",
    labelLocation: "स्थान",
    labelBio: "मेरे बारे में",
    labelInterests: "मेरी रुचियां",
    labelFields: "मेरे क्षेत्र",
    booksHeading: "मेरी किताबें",
    thTitle: "शीर्षक",
    thStatus: "स्थिति",
    thCreated: "बनाई गई",
    thModified: "संशोधित",
    thActions: "कार्रवाइयां",
    editButton: "संपादित करें",
    emptyState: "आपने अभी तक कोई किताब नहीं बनाई है।",
    loading: "आपकी किताबें लोड हो रही हैं...",
    error: "किताबें लोड करने में विफल। कृपया पुनः प्रयास करें।",
    loginRequired: "कृपया अपना खाता देखने के लिए लॉग इन करें।",
  },

  profile: {
    heading: "मेरी प्रोफ़ाइल बनाएं",
    editHeading: "प्रोफ़ाइल संपादित करें",
    labelDisplayName: "पूरा नाम / प्रदर्शन नाम",
    labelHeadline: "शीर्षक",
    labelProfileImage: "प्रोफ़ाइल चित्र",
    labelLocation: "स्थान",
    labelBio: "मेरे बारे में",
    placeholderDisplayName: "आपका प्रदर्शन नाम",
    placeholderHeadline: "जैसे, कहानियों और रेशम की भावुक बुनकर",
    labelHandle: "प्रोफ़ाइल URL आईडी",
    placeholderHandle: "जैसे, Chitra-Sharma",
    handleHelp: "आपका सार्वजनिक प्रोफ़ाइल लिंक। केवल अक्षर, अंक और हाइफ़न; आपके नाम से सुझाया गया।",
    handleChecking: "उपलब्धता जाँची जा रही है…",
    handleAvailable: "यह प्रोफ़ाइल लिंक उपलब्ध है।",
    handleTaken: "यह प्रोफ़ाइल URL आईडी पहले से ली जा चुकी है — कोई अंक या शब्द जोड़कर देखें।",
    handleInvalid: "2–40 अक्षर, अंक या हाइफ़न का उपयोग करें (केवल अंक नहीं)।",
    placeholderLocation: "शहर, देश",
    placeholderBio: "अपनी कहानी अपने शब्दों में बताएं...",
    imageAlt: "प्रोफ़ाइल चित्र",
    uploading: "अपलोड हो रहा है...",
    imageUploaded: "चित्र अपलोड हो गया!",
    imageUploadFailed: "चित्र अपलोड करने में विफल।",
    nameRequired: "प्रदर्शन नाम आवश्यक है।",
    labelInterests: "मैं बनाना चाहती हूं",
    labelFields: "मेरी रुचि के क्षेत्र",
    interestOptions: ["My Page", "Book", "Gallery", "Poems", "Blog", "Article", "Recipes", "DIY", "Other"],
    fieldOptions: ["Art", "Music", "Writing", "Tech", "Creativity", "Community"],
    saveButton: "प्रोफ़ाइल सहेजें",
    saving: "सहेज रहे हैं...",
    saved: "प्रोफ़ाइल सहेजी गई!",
    saveFailed: "प्रोफ़ाइल सहेजने में विफल। कृपया पुनः प्रयास करें।",
    backToAccount: "खाते पर वापस",
    loading: "प्रोफ़ाइल लोड हो रही है...",
    loadError: "प्रोफ़ाइल लोड करने में विफल।",
    loginRequired: "कृपया अपनी प्रोफ़ाइल संपादित करने के लिए लॉग इन करें।",
  },

  publicProfile: {
    loading: "प्रोफ़ाइल लोड हो रही है...",
    notFound: "प्रोफ़ाइल नहीं मिली।",
    memberSince: "सदस्य तब से",
    booksHeading: "प्रकाशित किताबें",
    noBooks: "अभी कोई प्रकाशित किताब नहीं है।",
  },

  publicBooks: {
    heading: "प्रकाशित किताबें",
    loading: "किताबें लोड हो रही हैं...",
    error: "किताबें लोड करने में विफल। कृपया पुनः प्रयास करें।",
    emptyState: "अभी कोई प्रकाशित किताब उपलब्ध नहीं है।",
    readButton: "पढ़ें",
    backToBooks: "किताबों पर वापस",
    home: "होम",
    loginPrompt: "अपनी किताबें बनाने और प्रबंधित करने के लिए लॉग इन करें।",
  },

  readBook: {
    backToSearch: "खोज पर वापस",
    home: "होम",
    books: "किताबें",
    prevBook: "पिछली किताब",
    nextBook: "अगली किताब",
  },

  flipBook: {
    gateKicker: (n, total) => `निःशुल्क पूर्वावलोकन · ${total} में से ${n} पृष्ठ`,
    gateTitle: "यह अंक पसंद आ रहा है?",
    gateTextLogin: "बाकी अंक पढ़ने के लिए लॉग इन करें। यह निःशुल्क है — हर सदस्य हर अंक पूरा पढ़ सकता है।",
    gateLogin: "जारी रखने के लिए लॉग इन करें",
    gateSignup: "नए हैं? निःशुल्क खाता बनाएँ",
    pageHeading: (n) => `पृष्ठ ${n}`,
    pageImageAlt: (n, i) => `पृष्ठ ${n} - ${i}`,
    prevPage: "पिछला पृष्ठ",
    nextPage: "अगला पृष्ठ",
    firstPage: "पहला पृष्ठ",
    lastPage: "अंतिम पृष्ठ",
    zoomIn: "ज़ूम इन",
    zoomOut: "ज़ूम आउट",
    zoomReset: "ज़ूम रीसेट",
    fullscreen: "फ़ुल स्क्रीन",
    exitFullscreen: "फ़ुल स्क्रीन बंद करें",
  },

  pageLayoutEditor: {
    labelBar: "पृष्ठ लेआउट संपादक \u2014 चित्रों को स्थिति में खींचें, आकार बदलने के लिए कोना खींचें",
    image1Label: "चित्र 1",
    image2Label: "चित्र 2",
  },

  category: {
    comingSoon: (title) => `${title} की सामग्री जल्द आ रही है। बने रहें!`,
    backToHome: "होम पर वापस",
  },

  categoryPage: {
    tabBrowse: "ब्राउज़ करें",
    tabCreate: "बनाएं",
    tabMyBooks: "मेरी किताबें",
    noPublished: "इस श्रेणी में अभी कोई प्रकाशित किताब नहीं है।",
    beFirst: "पहली किताब बनाएं!",
    loginToCreate: "सामग्री बनाने के लिए लॉग इन करें",
    createHeading: (title) => `${title} किताब बनाएं`,
    titlePlaceholder: "किताब का शीर्षक दर्ज करें...",
    createButton: "किताब बनाएं",
    createHint: "आपकी किताब ड्राफ्ट के रूप में बनाई जाएगी। पृष्ठ, चित्र जोड़ने और प्रकाशित करने के लिए किताबें पेज का उपयोग करें।",
    myBooksHeading: (title) => `मेरी ${title} किताबें`,
    noMyBooks: "आपने अभी तक इस श्रेणी में कोई किताब नहीं बनाई है।",
    createFirst: "अपनी पहली किताब बनाएं",
    enterTitle: "कृपया शीर्षक दर्ज करें",
    bookCreated: "किताब ड्राफ्ट के रूप में बनाई गई!",
    createFailed: "किताब बनाने में विफल",
    confirmDelete: "क्या यह किताब हटाएं?",
    bookDeleted: "किताब हटा दी गई!",
    deleteFailed: "किताब हटाने में विफल",
    bookPublished: "किताब प्रकाशित हो गई!",
    publishFailed: "प्रकाशित करने में विफल",
    backToBrowse: "वापस",
    descriptions: {
      art: "समुदाय के साथ कला सामग्री का अन्वेषण और साझा करें",
      music: "समुदाय के साथ संगीत सामग्री का अन्वेषण और साझा करें",
      writing: "समुदाय के साथ लेखन सामग्री का अन्वेषण और साझा करें",
      tech: "समुदाय के साथ तकनीक सामग्री का अन्वेषण और साझा करें",
      creativity: "समुदाय के साथ रचनात्मकता सामग्री का अन्वेषण और साझा करें",
      community: "समुदाय के साथ सामुदायिक सामग्री का अन्वेषण और साझा करें",
    },
  },

  techPage: {
    heading: "तकनीक",
    description: "कई आयामों में प्रौद्योगिकी विषयों का अन्वेषण करें",
    tabTechnical: "तकनीकी",
    tabCareer: "करियर और उद्योग",
    tabCommunity: "समुदाय और प्रतिनिधित्व",
    tabOther: "अन्य",
    descTechnical: "प्रोग्रामिंग, कोडिंग ट्यूटोरियल, टूल्स और तकनीकी संसाधन",
    descCareer: "तकनीकी करियर, नौकरी कौशल, उद्योग अंतर्दृष्टि और पेशेवर विकास",
    descCommunity: "तकनीक में महिलाएं, विविधता, समावेशन और प्रतिनिधित्व की कहानियां",
    descOther: "विविध तकनीकी विषय, रुझान और दिलचस्प खोजें",
    comingSoon: "सामग्री जल्द आ रही है। बने रहें!",
  },

  admin: {
    heading: "व्यवस्थापक डैशबोर्ड",
    tabStats: "आंकड़े",
    tabUsers: "उपयोगकर्ता",
    tabBooks: "किताबें",
    totalUsers: "कुल उपयोगकर्ता",
    activeUsers: "सक्रिय उपयोगकर्ता",
    blockedUsers: "अवरुद्ध उपयोगकर्ता",
    adminCount: "व्यवस्थापक",
    totalBooks: "कुल किताबें",
    publishedBooks: "प्रकाशित",
    draftBooks: "ड्राफ्ट",
    colName: "नाम",
    colEmail: "ईमेल",
    colRole: "भूमिका",
    colStatus: "स्थिति",
    colLastLogin: "अंतिम लॉगिन",
    colActions: "कार्रवाइयां",
    colTitle: "शीर्षक",
    colAuthor: "लेखक",
    colBookStatus: "स्थिति",
    colModified: "संशोधित",
    roleUpdated: "भूमिका अपडेट हो गई!",
    roleUpdateFailed: "भूमिका अपडेट करने में विफल",
    statusUpdated: "स्थिति अपडेट हो गई!",
    statusUpdateFailed: "स्थिति अपडेट करने में विफल",
    confirmDeleteBook: "क्या आप वाकई इस किताब को हटाना चाहते हैं?",
    bookDeleted: "किताब हटा दी गई!",
    bookDeleteFailed: "किताब हटाने में विफल",
  },

  chat: {
    roomsHeading: "चैट रूम",
    noMessages: "अभी तक कोई संदेश नहीं। बातचीत शुरू करें!",
    selectRoom: "चैट शुरू करने के लिए एक रूम चुनें",
    placeholder: "संदेश लिखें...",
    sendButton: "भेजें",
    sending: "...",
    deleteMessage: "हटाएं",
  },

  messages: {
    title: "संदेश",
    headerTitle: "संदेश",
    roleBuyer: "खरीदार",
    roleSeller: "विक्रेता",
    emptyTitle: "अभी कोई बातचीत नहीं।",
    emptyHint: "बिक्री के लिए चिह्नित कोई कलाकृति खोजें और बातचीत शुरू करने के लिए 'विक्रेता से संपर्क करें' दबाएँ।",
    browseGalleries: "गैलरी देखें",
    noMessagesYet: "अभी कोई संदेश नहीं",
    pickThread: "पढ़ने के लिए कोई बातचीत चुनें।",
    firstHint: "नमस्ते कहें और कलाकृति के बारे में पूछें — आकार, मूल्य, शिपिंग।",
    composePlaceholder: "संदेश लिखें…",
    send: "भेजें",
    sendFailed: "भेजा नहीं जा सका। कृपया पुनः प्रयास करें।",
    loadError: "यह बातचीत लोड नहीं हो सकी।",
    closed: "बंद",
    closedHint: "यह बातचीत बंद कर दी गई है।",
    privacyNote: "यह आपके और दूसरे पक्ष के बीच निजी है। सुरक्षा के लिए सात सहेली के एडमिन बातचीत की समीक्षा कर सकते हैं।",
    lockedText: "अपने संदेश देखने के लिए कृपया लॉग इन करें।",
    lockedCta: "लॉग इन करें",
    guestBadge: "अतिथि",
    guestBanner: "इस खरीदार ने बिना खाते के आपसे संपर्क किया है। उन्हें ईमेल या फ़ोन से जवाब दें — नीचे लिखी गई कोई भी बात आपके अपने नोट के रूप में रखी जाती है।",
    guestReplyEmail: "ईमेल से जवाब दें",
    guestCall: "कॉल करें",
    guestNotePlaceholder: "अपने रिकॉर्ड के लिए निजी नोट…",
  },

  gallery: {
    heading: "फोटो गैलरी",
    forSale: "बिक्री के लिए",
    sold: "बिक गया",
    askPrice: "मूल्य पूछें",
    contactCreator: "विक्रेता से संपर्क करें",
    bySeller: "द्वारा",
    guestTitle: "विक्रेता से संपर्क करें",
    guestLoginInstead: "खाता है? चैट के लिए लॉग इन करें",
    guestName: "आपका नाम",
    guestEmail: "ईमेल पता",
    guestPhone: "फ़ोन नंबर",
    guestMessage: "आपका संदेश — आकार, मूल्य, शिपिंग…",
    guestSend: "विक्रेता को भेजें",
    guestNeedName: "कृपया अपना नाम दर्ज करें।",
    guestNeedEmail: "कृपया एक मान्य ईमेल पता दर्ज करें।",
    guestNeedPhone: "कृपया एक मान्य फ़ोन नंबर दर्ज करें।",
    guestNeedMessage: "कृपया एक संदेश लिखें।",
    guestNeedCaptcha: "कृपया reCAPTCHA पूरा करें।",
    guestFailed: "आपका संदेश भेजा नहीं जा सका। कृपया पुनः प्रयास करें।",
    guestSentTitle: "भेज दिया!",
    guestSentText: "विक्रेता को आपका संदेश मिल गया है और वे आपके ईमेल या फ़ोन पर जवाब देंगे।",
    guestPrivacy: "आपका विवरण केवल इस विक्रेता (और सुरक्षा के लिए सात सहेली एडमिन) के साथ साझा किया जाता है।",
    back: "वापस",
    noPhotos: "इस गैलरी में अभी कोई फोटो नहीं है।",
    writeComment: "टिप्पणी लिखें...",
    postComment: "पोस्ट करें",
    noComments: "अभी तक कोई टिप्पणी नहीं। पहले बनें!",
    commentsHeading: (n) => `टिप्पणियां (${n})`,
    deleteComment: "हटाएं",
    loginToComment: "टिप्पणी करने के लिए लॉग इन करें",
    photos: (n) => `${n} फोटो`,
  },

  magazine: {
    heading: "सात सहेली पत्रिका",
    loading: "पत्रिका लोड हो रही है...",
    notFound: "पत्रिका अभी उपलब्ध नहीं है।",
    pageOf: (current, total) => `पृष्ठ ${current} / ${total}`,
    pickEdition: "पढ़ने के लिए एक संस्करण चुनें",
    backToList: "पत्रिकाओं पर वापस जाएं",
  },

  magazineEditor: {
    heading: "पत्रिका संपादक",
    createMagazine: "पत्रिका बनाएं",
    editPage: "पृष्ठ संपादित करें",
    addPage: "पृष्ठ जोड़ें",
    deletePage: "पृष्ठ हटाएं",
    backgroundColor: "पृष्ठभूमि रंग",
    borderStyle: "बॉर्डर शैली",
    borderColor: "बॉर्डर रंग",
    borderWidth: "बॉर्डर चौड़ाई",
    addTextBlock: "+ टेक्स्ट",
    addImageBlock: "+ छवि",
    textContent: "टेक्स्ट सामग्री",
    fontFamily: "फ़ॉन्ट",
    fontSize: "आकार",
    fontColor: "रंग",
    publish: "प्रकाशित करें",
    saveDraft: "ड्राफ्ट सहेजें",
    unpublish: "अप्रकाशित करें",
    publishSuccess: "पत्रिका सफलतापूर्वक प्रकाशित!",
    unpublishSuccess: "पत्रिका अप्रकाशित (ड्राफ्ट में वापस)",
    saveSuccess: "पृष्ठ सफलतापूर्वक सहेजा गया!",
    deleteSuccess: "पृष्ठ सफलतापूर्वक हटाया गया!",
    draftSaved: "ड्राफ्ट सहेजा गया!",
    pageNumber: "पृष्ठ",
    emptyPage: "खाली",
    preview: "पूर्वावलोकन",
    uploadImage: "छवि अपलोड करें",
    backToEditor: "संपादक पर वापस",
    newEdition: "+ नया संस्करण",
    editions: "संस्करण",
    untitled: "बिना शीर्षक",
    pages: "पृष्ठ",
    cover: "कवर",
    back: "पिछला",
    bg: "पृष्ठभूमि",
    border: "बॉर्डर",
    none: "कोई नहीं",
    solid: "ठोस",
    dashed: "डैश",
    dotted: "बिंदु",
    double: "दोहरा",
    enterText: "टेक्स्ट दर्ज करें...",
    bold: "बोल्ड",
    italic: "इटैलिक",
    underline: "रेखांकन",
    left: "बाएं",
    center: "बीच में",
    right: "दाएं",
    remove: "हटाएं",
    replace: "बदलें",
    fit: "फ़िट",
    fillCrop: "भरें (काटें)",
    fitShowFull: "फ़िट (पूरा दिखाएं)",
    stretch: "खींचें",
    opacity: "पारदर्शिता",
    fitToPage: "पृष्ठ में फ़िट",
    fillPage: "पृष्ठ भरें",
    uploading: "अपलोड हो रहा है...",
    importing: "आयात हो रहा है...",
    uploadDoc: "PDF/Word अपलोड करें",
    saving: "सहेजा जा रहा है...",
    savePage: "पृष्ठ सहेजें",
    canvasHint: "खींचें और छोड़ें — कोने से आकार बदलें",
    pageTextLabel: "पृष्ठ टेक्स्ट (खोज और पढ़ने के लिए — वैकल्पिक)",
    pageTextPlaceholder: "वैकल्पिक सादा टेक्स्ट...",
    newEditionPrompt: "नए पत्रिका संस्करण का शीर्षक दर्ज करें:",
    newEditionCreated: "नया संस्करण बनाया गया!",
    docImported: "दस्तावेज़ पृष्ठ आयातित!",
    importModePrompt: "इस पत्रिका में पहले से पृष्ठ हैं। क्या आप उन्हें इस PDF से बदलना चाहते हैं (पहला पृष्ठ कवर बनेगा), या नए पृष्ठ अंत में जोड़ना चाहते हैं?",
    importModeReplace: "बदलें",
    importModeAppend: "जोड़ें",
    importModeCancel: "रद्द करें",
  },

  dropdown: {
    defaultText: "चुनें",
  },
};

// Default export for backward compatibility during transition
const strings = en;
export default strings;
